/**
 * Product Selection Modal Functionality
 * Handles the modal interaction and cart integration
 */

(function() {
  'use strict';

  let selectedVariantId = null;
  let selectedOption = 'trial';

  /** Reparent to body so position:fixed covers the viewport (not a transformed section). */
  function ensureModalOnBody(modal) {
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  function moveAllProductSelectionModalsToBody() {
    document.querySelectorAll('.product-selection-modal-overlay').forEach(ensureModalOnBody);
  }

  // Open modal
  window.openProductSelectionModal = function(sectionId) {
    const modal = document.getElementById(`ProductSelectionModal-${sectionId}`);
    if (modal) {
      ensureModalOnBody(modal);
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Move subscription options into modal if they exist
      moveSubscriptionOptionsToModal(sectionId);
      
      // Set initial selection
      const primaryCard = modal.querySelector('.product-selection-option-card--primary');
      if (primaryCard) {
        selectedVariantId = primaryCard.dataset.variantId;
        selectedOption = primaryCard.dataset.option || 'trial';
        
        // Ensure primary card is selected
        const allCards = modal.querySelectorAll('.product-selection-option-card');
        allCards.forEach(card => {
          card.classList.remove('selected');
        });
        primaryCard.classList.add('selected');
        
        // Update form variant input
        const variantInputs = document.querySelectorAll('input[name="id"].product-variant-id');
        variantInputs.forEach(input => {
          input.value = selectedVariantId;
          input.disabled = false;
        });
      }
    }
  };

  // Move subscription options from product page into modal
  function moveSubscriptionOptionsToModal(sectionId) {
    const subscriptionContainer = document.getElementById(`ProductSelectionModal-SubscriptionContent-${sectionId}`);
    const subscriptionSection = document.getElementById(`ProductSelectionModal-SubscriptionOptions-${sectionId}`);
    
    if (!subscriptionContainer || !subscriptionSection) return;

    // Look for subscription options in various possible locations
    // Try common selectors for subscription apps (Seal Subscriptions, ReCharge, etc.)
    const possibleSelectors = [
      '[data-subscription-widget]',
      '[id*="subscription"]',
      '[class*="subscription"]',
      '[class*="purchase-option"]',
      '[class*="seal-subscription"]',
      'form[action*="cart"] [name*="subscription"]',
      '.product-form__subscription',
      '[data-product-subscription]'
    ];

    let subscriptionElement = null;
    
    // Try to find subscription options
    for (const selector of possibleSelectors) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        // Check if this element contains subscription-related content
        const text = el.textContent || el.innerText || '';
        if (text.includes('Purchase options') || 
            text.includes('One-time purchase') || 
            text.includes('Subscription Plans') ||
            text.includes('Seal Subscriptions') ||
            (el.querySelector('input[type="radio"]') && text.includes('subscription'))) {
          subscriptionElement = el;
          return;
        }
      });
      if (subscriptionElement) break;
    }

    // If found, move it to the modal
    if (subscriptionElement) {
      // Clone the element to avoid removing it from the original location
      const clonedElement = subscriptionElement.cloneNode(true);
      
      // Hide the original element (it should already be hidden, but ensure it)
      subscriptionElement.style.display = 'none';
      subscriptionElement.setAttribute('data-moved-to-modal', 'true');
      
      // Clear container and add cloned content
      subscriptionContainer.innerHTML = '';
      subscriptionContainer.appendChild(clonedElement);
      
      // Show the subscription section in modal
      subscriptionSection.style.display = 'block';
      
      // Re-initialize any scripts that might be needed
      initializeSubscriptionWidgets(subscriptionContainer);
    } else {
      // Hide subscription section if not found
      subscriptionSection.style.display = 'none';
    }
    
    // DEBUGGING: Temporarily disabled - showing subscription options on product page
    // Also ensure any remaining subscription options are hidden
    // hideSubscriptionOptionsOnPage();
  }

  // Initialize subscription widgets after moving to modal
  function initializeSubscriptionWidgets(container) {
    // Look for and reinitialize any subscription-related scripts
    const scripts = container.querySelectorAll('script');
    scripts.forEach(script => {
      if (script.src) {
        // External script - might need to reload
        const newScript = document.createElement('script');
        newScript.src = script.src;
        newScript.async = script.async;
        newScript.defer = script.defer;
        container.appendChild(newScript);
      } else if (script.textContent) {
        // Inline script - execute it
        try {
          eval(script.textContent);
        } catch (e) {
          console.warn('Could not execute subscription script:', e);
        }
      }
    });

    // Trigger any custom events that subscription widgets might listen to
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('subscription-widget-loaded'));
    }
  }

  // Close modal
  window.closeProductSelectionModal = function(event) {
    // If event is provided and it's not the overlay, don't close
    if (event && event.target !== event.currentTarget) {
      return;
    }

    const modals = document.querySelectorAll('.product-selection-modal-overlay.active');
    modals.forEach(modal => {
      modal.classList.remove('active');
      
      // Restore subscription options to original location if they were moved
      restoreSubscriptionOptions();
    });
    document.body.style.overflow = 'auto';
  };

  // Restore subscription options to original location (but keep them hidden)
  function restoreSubscriptionOptions() {
    const movedElements = document.querySelectorAll('[data-moved-to-modal="true"]');
    movedElements.forEach(el => {
      // Keep them hidden - we don't want them on the product page
      el.style.display = 'none';
      el.removeAttribute('data-moved-to-modal');
    });
  }

  // Toggle expandable section
  window.toggleProductSelectionExpand = function() {
    const expandableHeaders = document.querySelectorAll('.product-selection-expandable-header');
    expandableHeaders.forEach(header => {
      const content = header.nextElementSibling;
      const icon = header.querySelector('.product-selection-expandable-header__icon');
      
      if (content && content.classList.contains('product-selection-expandable-content')) {
        if (content.classList.contains('expanded')) {
          content.classList.remove('expanded');
          if (icon) icon.classList.remove('rotated');
        } else {
          content.classList.add('expanded');
          if (icon) icon.classList.add('rotated');
        }
      }
    });
  };

  // Select option
  window.selectProductOption = function(variantId, option) {
    selectedVariantId = variantId;
    selectedOption = option;
    
    // Remove selected class from all cards
    const allCards = document.querySelectorAll('.product-selection-option-card');
    allCards.forEach(card => {
      card.classList.remove('selected');
    });
    
    // Add selected class to clicked card
    const selectedCard = document.querySelector(`[data-option="${option}"]`);
    if (selectedCard) {
      selectedCard.classList.add('selected');
    }

    // Update the form's variant input immediately so it's ready for submission
    const variantInputs = document.querySelectorAll('input[name="id"].product-variant-id');
    variantInputs.forEach(input => {
      input.value = variantId;
      input.disabled = false;
    });
  };

  // Add selected product to cart
  window.addSelectedProductToCart = function(sectionId, productFormId) {
    if (!selectedVariantId) {
      console.error('No variant selected');
      return;
    }

    const form = document.getElementById(productFormId);
    if (!form) {
      console.error('Product form not found');
      return;
    }

    // Update variant ID in form
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = selectedVariantId;
      variantInput.disabled = false;
    }

    // Get the product-form custom element
    const productForm = form.closest('product-form');
    if (!productForm) {
      console.error('Product form element not found');
      return;
    }

    // Disable the modal button to prevent double submission
    const modalButton = document.querySelector(`#ProductSelectionModal-${sectionId} .product-selection-modal__primary-action`);
    if (modalButton) {
      modalButton.disabled = true;
      modalButton.textContent = 'Adding to cart...';
    }

    // Create a temporary submit button if one doesn't exist
    let submitButton = form.querySelector('[type="submit"]');
    if (!submitButton) {
      submitButton = document.createElement('button');
      submitButton.type = 'submit';
      submitButton.style.display = 'none';
      form.appendChild(submitButton);
    }

    // Trigger the product-form's submit handler
    // The product-form element handles the actual cart submission
    if (productForm.form) {
      // Dispatch submit event which will be caught by product-form's onSubmitHandler
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      productForm.form.dispatchEvent(submitEvent);
    } else {
      // Fallback: directly submit the form
      form.submit();
    }

    // Close modal after a short delay
    setTimeout(() => {
      closeProductSelectionModal();
      if (modalButton) {
        modalButton.disabled = false;
        modalButton.textContent = 'Continue to Cart';
      }
    }, 300);
  };

  // Close modal on ESC key
  document.addEventListener('keydown', function(event) {
    if (event.key === 'Escape') {
      const activeModal = document.querySelector('.product-selection-modal-overlay.active');
      if (activeModal) {
        closeProductSelectionModal();
      }
    }
  });

  // DEBUGGING: Temporarily disabled - showing subscription options on product page
  // Hide subscription options on product page (but not in cart drawer)
  function hideSubscriptionOptionsOnPage() {
    // DISABLED FOR DEBUGGING - uncomment to re-enable hiding
    return;
    /*
    // Only hide subscription options in product info area, not in cart drawer
    const productInfoWrapper = document.querySelector('.product__info-wrapper');
    if (!productInfoWrapper) return;

    // Look for subscription options and hide them
    const possibleSelectors = [
      '[data-subscription-widget]',
      '[id*="subscription"]:not([id*="Modal"])',
      '[class*="subscription"]:not([class*="Modal"]):not([class*="selection"])',
      '[class*="purchase-option"]:not([class*="Modal"])',
      '[class*="seal-subscription"]:not([class*="Modal"])',
      '.product-form__subscription',
      '[data-product-subscription]',
      '[class*="seal"]:not([class*="Modal"])',
      '[id*="seal"]:not([id*="Modal"])',
      '[data-seal]'
    ];

    possibleSelectors.forEach(selector => {
      try {
        // Only search within product info wrapper, not cart drawer
        const elements = productInfoWrapper.querySelectorAll(selector);
        elements.forEach(el => {
          // Skip if element is inside cart drawer
          if (el.closest('cart-drawer') || el.closest('#CartDrawer')) {
            return;
          }
          
          const text = el.textContent || el.innerText || '';
          if (text.includes('Purchase options') || 
              text.includes('One-time purchase') || 
              text.includes('Subscription Plans') ||
              text.includes('Seal Subscriptions') ||
              text.includes('Subscription details') ||
              (el.querySelector('input[type="radio"]') && text.includes('subscription'))) {
            el.style.display = 'none';
            el.setAttribute('data-hidden-for-modal', 'true');
          }
        });
      } catch (e) {
        // Ignore selector errors
      }
    });
    */
  }

  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    moveAllProductSelectionModalsToBody();
    // Ensure modal is hidden on load
    const modals = document.querySelectorAll('.product-selection-modal-overlay');
    modals.forEach(modal => {
      modal.classList.remove('active');
    });
    
    // DEBUGGING: Temporarily disabled - showing subscription options on product page
    // Hide subscription options on page load
    // hideSubscriptionOptionsOnPage();
    
    // Also hide after a short delay to catch dynamically loaded content
    // setTimeout(hideSubscriptionOptionsOnPage, 500);
    // setTimeout(hideSubscriptionOptionsOnPage, 1000);
    // setTimeout(hideSubscriptionOptionsOnPage, 2000);
  });

})();

